import React, { useCallback, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Dropdown, Slider, Stack } from '@fluentui/react';
import { getStyles } from './get-styles';
import { BingMapsSearchBox } from '../bing-maps-search-box/index';
import { BingMapsWithZoomButton } from '../bing-maps-view/index';
import {
  LocationTarget,
} from '../util/index';

function LocationRadiusEditorUnstyled(props) {
  const {
    classes,
    searchBoxHeader,
    radiusSize,
    radiusUnit,
    locationTargets,
    maxRadiusInKilometers,
    maxRadiusInMiles,
    minRadiusInKilometers,
    minRadiusInMiles,
    onLocationTargetsChange,
    onRadiusSizeChange,
    onRadiusUnitChange,
    searchChangeDebounceWait,
    placeholder,
    noResultsMessage,
    searchResultsFooter,
    searchResultsFooterOnClick,
  } = props;

  const i18n = useContext(I18nContext);

  const getIsBoundaryTarget = useCallback(() => false, []);
  const mapRef = useRef();

  return (
    <div className={`bing-maps-location-radius-editor  ${classes.bingMapsLocationTargetsEditor}`}>
      <div className="bing-maps-location-target-and-map">
        <div className="bing-maps-location-targets-search-grid">
          <div className="search-box">
            <div className="search-box-header" id="search-box-header">{searchBoxHeader}</div>
            <BingMapsSearchBox
              getIsSearchResultBoundaryTarget={getIsBoundaryTarget}
              locationTargets={locationTargets}
              changeDebounceWait={searchChangeDebounceWait}
              onLocationTargetsChange={onLocationTargetsChange}
              radiusSize={radiusSize}
              radiusUnit={radiusUnit}
              isExcludeEnabled={false}
              isSingleLocationSelect
              placeholder={placeholder}
              noResultsMessage={noResultsMessage}
              searchResultsFooter={searchResultsFooter}
              searchResultsFooterOnClick={searchResultsFooterOnClick}
              ariaLabel={searchBoxHeader || i18n.getString(_TL_('Business location'))}
            />
          </div>

          <Stack horizontal verticalAlign="end" horizontalAlign="end" className="radius-slider">
            <Stack.Item grow disableShrink>
              <Slider
                className="slider"
                label={i18n.getString(_TL_('Radius'))}
                max={radiusUnit === 'mi' ? maxRadiusInMiles : maxRadiusInKilometers}
                min={radiusUnit === 'mi' ? minRadiusInMiles : minRadiusInKilometers}
                value={radiusSize}
                showValue
                onChange={onRadiusSizeChange}
                ariaValueText={value => `${value} ${radiusUnit === 'mi' ? i18n.getString(_TL_('miles')) : i18n.getString(_TL_('km'))}`}
              />
            </Stack.Item>
            <Dropdown
              aria-label={i18n.getString(_TL_('radius distance unit'))}
              className="dropdown"
              defaultSelectedKey={radiusUnit}
              options={[
                { key: 'mi', text: i18n.getString(_TL_('miles')), title: i18n.getString(_TL_('miles')) },
                { key: 'km', text: i18n.getString(_TL_('km')), title: i18n.getString(_TL_('km')) },
              ]}
              onChange={onRadiusUnitChange}
              styles={{ dropdown: { width: 85 } }}
            />
          </Stack>
        </div>
        <BingMapsWithZoomButton
          mapRef={mapRef}
          locationTargets={locationTargets}
        />
      </div>
    </div>
  );
}

LocationRadiusEditorUnstyled.propTypes = {
  classes: PropTypes.shape({ bingMapsLocationTargetsEditor: PropTypes.string }).isRequired,
  searchBoxHeader: PropTypes.string.isRequired,
  radiusSize: PropTypes.number,
  radiusUnit: PropTypes.oneOf(['mi', 'km']),
  locationTargets: PropTypes.arrayOf(PropTypes.instanceOf(LocationTarget)).isRequired,
  maxRadiusInKilometers: PropTypes.number,
  maxRadiusInMiles: PropTypes.number,
  minRadiusInKilometers: PropTypes.number,
  minRadiusInMiles: PropTypes.number,
  onLocationTargetsChange: PropTypes.func.isRequired,
  onRadiusSizeChange: PropTypes.func.isRequired,
  onRadiusUnitChange: PropTypes.func.isRequired,
  searchChangeDebounceWait: PropTypes.number,
  placeholder: PropTypes.string,
  noResultsMessage: PropTypes.string,
  searchResultsFooter: PropTypes.func,
  searchResultsFooterOnClick: PropTypes.func,
};

LocationRadiusEditorUnstyled.defaultProps = {
  radiusSize: 25,
  radiusUnit: 'mi',
  maxRadiusInKilometers: 80,
  maxRadiusInMiles: 50,
  minRadiusInKilometers: 3,
  minRadiusInMiles: 3,
  searchChangeDebounceWait: 100,
  placeholder: '',
  noResultsMessage: '',
  searchResultsFooter: null,
  searchResultsFooterOnClick: _.noop,
};

export const LocationRadiusEditor = withDefaultStyles(LocationRadiusEditorUnstyled, getStyles);
